import { inject, Injectable } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { filter, map, mergeWith, shareReplay, Subject } from 'rxjs';
import { PageMetadata } from './page-metadata.interfaces';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';

@Injectable({ providedIn: 'root' })
export class PageMetadataService {
  private readonly router = inject(Router);
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);
  private readonly activatedRoute = inject(ActivatedRoute);

  private pageMetadata$ = this.router.events.pipe(
    filter((e) => e instanceof NavigationEnd),
    map(() => {
      let route: ActivatedRoute | undefined = this.activatedRoute;
      let metadata: PageMetadata = this.getDefaultPageMetadata();
      while (route?.outlet === 'primary') {
        Object.assign(metadata, route.snapshot.data['metadata']);
        // Use title from metadata, route config, or preserve, in that order.
        Object.assign(metadata, {
          title:
            route.snapshot.data['metadata']?.title ??
            route.routeConfig?.title ??
            metadata.title,
        });
        route = route.children.find((r) => r.outlet === 'primary');
      }
      return metadata;
    }),
    shareReplay(1),
  );
  private readonly updateMetadataSubject = new Subject<Partial<PageMetadata>>();
  // Priority is used to deal with race conditions; most sources of page metadata arrive following either
  // route navigation events, guard or component initialization. The order in which those arrive is unstable.
  // The source of the metadata can therefore specify a priority, and in each window between 2 NavigationStart
  // events of the router, the highest priority metadata will remain active.
  private priority = -1;

  constructor() {
    // Reset priority every time a new navigation starts.
    this.router.events
      .pipe(filter((e) => e instanceof NavigationStart))
      .subscribe(() => (this.priority = -1));

    this.pageMetadata$
      .pipe(
        mergeWith(this.updateMetadataSubject),
        takeUntilDestroyed(),
        filter((m) => (m.priority ?? 0) > this.priority),
      )
      .subscribe((metadata) => {
        this.priority = metadata.priority ?? 0;
        if (metadata.title) this.title.setTitle(metadata.title);
        if (metadata.description)
          this.meta.updateTag({
            name: 'description',
            content: metadata.description,
          });
        if (metadata.keywords) {
          if (metadata.keywords.length > 0) {
            this.meta.updateTag({
              name: 'keywords',
              content: metadata.keywords.join(','),
            });
          } else {
            this.meta.removeTag('name="keywords"');
          }
        }
      });
  }

  public updateMetadata(metadata: Partial<PageMetadata>) {
    metadata.priority ??= 1;
    this.updateMetadataSubject.next(metadata);
  }

  private getDefaultPageMetadata(): PageMetadata {
    return {
      title: $localize`:default page title:Skillz`,
      description: $localize`:default page description:Discover the most exciting offerings from Skillz vzw! Holiday camps and workshops for inventors, artists, and gamers between 8 and 14 years old in Flanders!`,
      keywords: [
        $localize`:default page keyword:STEAM Camps`,
        $localize`:default page keyword:STEM`,
        $localize`:default page keyword:Workshops`,
        $localize`:default page keyword:Robotics`,
        $localize`:default page keyword:Coding`,
        $localize`:default page keyword:Minecraft`,
        $localize`:default page keyword:creative holiday camps`,
        $localize`:default page keyword:technology holiday camps`,
        $localize`:default page keyword:gaming holiday camps`,
        $localize`:default page keyword:Children`,
        $localize`:default page keyword:Teens`,
        $localize`:default page keyword:Youth`,
        $localize`:default page keyword:Flanders`,
        $localize`:default page keyword:Summer camps in Flanders`,
        $localize`:default page keyword:Camps nearby`,
        $localize`:default page keyword:Holiday camps`,
        $localize`:default page keyword:Summer camps`,
        $localize`:default page keyword:Autumn break`,
        $localize`:default page keyword:Easter break`,
        $localize`:default page keyword:Carnival break`,
        $localize`:default page keyword:Skillz`,
        $localize`:default page keyword:Skillz non-profit`,
      ],
    } as PageMetadata;
  }
}
